<template>
  <div class="custom-number-input-container">
    <Icon v-if="icon" class="custom-number-input-icon" :icon="icon"/>
    <input 
      class="custom-number-input" 
      :class="{'has-error': error, 'has-error-fixed': false, 'has-icon': icon, disabled: disabled, 'disabled-visible': disabledVisible }"
      type="number" 
      :min="min"
      :max="max"
      :placeholder="placeholder"
      :value="displayValue"
      @blur="handleInput($event, true)"
      @input="handleInput($event, false)"
      @keydown.delete="handleDelete"
    >
    <div v-if="units" class="custom-number-input-select-unit">
      <CustomButton :level="$enums.ButtonLevels.UnitSwitcher" ref="unitSwitcher" @click="openBurgerUnits">
        <div class="token-text-micro">
          {{ selectedUnit?.label }}
        </div>
        <Icon icon="caret-down" />
      </CustomButton>
      <DropdownMenu 
        :items="unitBurgerList"
        v-model:show="showBurgerUnits"
        :maxContent="true"
        @update:modelValue="onUserUnitChange"
        :target="$refs.unitSwitcher"
        classInternal="my-4"
      />
    </div>
    <div v-else-if="selectedUnit?.label" class="custom-number-input-select-unit">
      <div class="token-text-micro">
        {{ selectedUnit?.label }}
      </div>
    </div>
    <div class="custom-number-input-buttons" v-if="!disabled">
      <button class="up" @click="numberUp" :disabled="disabled ? true : (modelValue !== null && max !== undefined ? parseInt(modelValue) >= max : false)"><Icon icon="caret-up-small"/></button>
      <button class="down" @click="numberDown" :disabled="disabled ? true : (modelValue !== null && min !== undefined ? parseInt(modelValue) <= min : false)"><Icon icon="caret-down-small"/></button>
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Options, prop } from 'vue-class-component';
import Icon from '@/components/UIElements/Icon.vue'
import { nextTick } from 'vue';
import { Unit } from '@/types';
import CustomButton from '@/components/UIElements/CustomButton.vue';
import DropdownMenu from '@/components/UIElements/DropdownMenu.vue';
import { ListItem } from '@/components/UIElements/List.vue';
import { Watch } from 'vue-property-decorator';

class Props {
  placeholder?: string = prop({
    required: false,
  });
  nullable?: boolean = prop({
    required: false,
  });
  error?: string = prop({
    required: false,
  });
  icon?: string = prop({
    required: false,
  });
  disabled?: boolean = prop({
    required: false,
  });
  disabledVisible?: boolean = prop({
    required: false,
  });
  modelValue: string | null = prop({
    required: true,
  });
  min?: number = prop({
    required: false,
  });
  max?: number = prop({
    required: false,
  });
  delay?: number = prop({
    required: false,
  });
  units?:Unit[] = prop({
    required: false,
  });  
  selectedUnit?:Unit = prop({
    required: false,
  });
}


@Options({
  components: {
    Icon,
    CustomButton,
    DropdownMenu
  },
})
export default class CustomTextInput extends Vue.with(Props) {
  timeoutDelay = 1500
  timeout?:number
  showBurgerUnits = false

  correctedValue:number | null = 0

  mounted(): void {
    if(this.delay) {
      this.timeoutDelay = this.delay
    }
  }

  get currentRatio():number {
    return this.selectedUnit?.ratio ?? 1
  }

  get displayValue():string | null {
    if(this.correctedValue === null && this.nullable) {
      return null
    } else if (this.correctedValue !== null) {
      return (this.correctedValue / this.currentRatio).toString()
    }
    return null
  }

  @Watch('modelValue', { immediate: true })
  onModelValueChange() {
    if(this.modelValue !== null) {
      this.correctedValue = parseInt(this.modelValue)
    } else if(this.nullable) {
      this.correctedValue = null
    } else {
      this.correctedValue = 0
    }
  }

  @Watch('selectedUnit', { immediate: true })
  onUnitChange() {
    if(!this.selectedUnit?.ratio) return;

    let value = this.modelValue !== null ? parseInt(this.modelValue)  : this.modelValue
    if(this.min !== undefined && value !== null && value < this.min) {
      this.correctedValue = this.min
    } else if(value !== null) {
      this.correctedValue = value 
    } else if(this.nullable) {
      this.correctedValue = null
    } else {
      this.correctedValue = 0
    }

  }

  onUserUnitChange(unitListItem:ListItem) {
    const newSelectedUnit = {
      label: unitListItem.text as string,
      ratio: parseInt(unitListItem.value as string)
    }
    this.showBurgerUnits = false
    this.$emit('update:selectedUnit', newSelectedUnit)
  }

  openBurgerUnits() {
    this.showBurgerUnits = true
  }

  get unitBurgerList():ListItem[] {
    const ret:ListItem[] = []
    this.units?.forEach((unit, index) => {
      ret.push({
        id: 'unit-'+index,
        value: unit.ratio.toString(),
        text: unit.label,
        selected: unit.ratio === this.selectedUnit?.ratio
      })
    })
    return ret
  }

  handleDelete(event:Event) {
    const target = event.target as HTMLInputElement
    event.preventDefault()
    let value:number | null = target?.value.length > 1 ? parseInt(target.value.slice(0, -1)) : (this.nullable ? null : 0)
    value = value !== null ? value * this.currentRatio : null

    if(this.min !== undefined && value !== null && value < this.min) {
      this.correctedValue = this.min
    } else {
      this.correctedValue = value
    }
    
    if(this.nullable && value === null) {
      this.$emit('update:modelValue', null)
    } else {
      this.$emit('update:modelValue', this.correctedValue)
    }
    this.onValueChage()
  }
      

  handleInput (event:Event, instant:boolean) {
    const target = event.target as HTMLInputElement
    let value = target?.value ? parseInt(target?.value) : (this.nullable ? null : 0)
    value = value !== null ? value * this.currentRatio : null

    console.log(value, this.min, this.max)
    if(value !== null) {
      if(this.min !== undefined) {
        if(value <= this.min) {
          value = this.min
        }
      }
      if(this.max !== undefined) {
        if(value >= this.max) {
          value = this.max
        }
      }
    }

    this.correctedValue = value
    nextTick(() => {
      this.$emit('update:modelValue', this.correctedValue)
      if(instant) {
        this.onBlur()
      } else {
        this.onValueChage()
      }
    })
  }

  onBlur() {
    if(this.timeout) {
      clearTimeout(this.timeout)
      this.$emit('delayedUpdate', this.correctedValue)
    }
  }

  numberUp () {
    const diff = 1 * this.currentRatio
    const newVal = this.modelValue !== null ? (typeof this.modelValue === 'string' ? parseInt(this.modelValue) + diff : this.modelValue + diff) : (this.min !== undefined ? this.min : 1);
    this.correctedValue = this.max !== undefined ? (newVal <= this.max ? newVal : this.max) : newVal
    this.$emit('update:modelValue', this.correctedValue)

    this.onValueChage()
  }
  numberDown () {
    const diff = 1 * this.currentRatio
    const newVal = this.modelValue ? parseInt(this.modelValue) - diff :  (this.min !== undefined ? this.min : 0);
    this.correctedValue = this.min !== undefined ? (newVal >= this.min ? newVal : this.min) : newVal
    this.$emit('update:modelValue', this.correctedValue)

    this.onValueChage()
  }

  beforeUnmount() {
    this.onBlur()
  }

  onValueChage() {
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => {
      this.$emit('delayedUpdate', this.correctedValue)
      clearTimeout(this.timeout)
      this.timeout = undefined
    }, this.timeoutDelay)
  }

}
</script>

<style lang="scss" scoped>
@import '@/css/variables';

.custom-number-input-container {
  position: relative;
  input[type=number]::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }
  input[type=number] {
    -moz-appearance:textfield;
  }
  .custom-number-input-icon {
    position: absolute;
    top: 21px;
    left: 22px;
  }

  .custom-number-input-select-unit {
    display: flex;
    flex-direction: column;
    position: absolute;
    top: 8px;
    right: 32px;
  }

  .custom-number-input-buttons {
    display: flex;
    flex-direction: column;
    position: absolute;
    top: 6px;
    right: 10px;
    button {
      border: 1px solid $color-neutral-grey-8;
      box-shadow: $shadow-light-btn;
      background: $color-background-button-text-secondary-normal;
      width: 16px;
      height: 12px;
      &:hover {
        box-shadow: $shadow-light-btn-hover;
        background: $color-background-button-text-secondary-hover;
      }
      &:active {
        box-shadow: $shadow-light-btn-pressed;
        background: $color-background-button-text-secondary-pressed;
      }
      &:disabled {
        box-shadow: none;
        background: $color-background-button-text-secondary-disabled !important;
      }
      &.disabled {
        box-shadow: none;
        background: $color-background-button-text-secondary-disabled !important;
      }
      >span {
        width: 12px;
      }
    }
    .up {
      border-radius: 8px 8px 0px 0px;
    }
    .down {
      border-radius: 0px 0px 8px 8px;
    }
  }
  .custom-number-input {
    width: 100%;
    height: 38px;
    font-weight: 400;
    font-size: 14px;
    line-height: 24px;
    background: white;
    border: 1px solid $color-neutral-grey-12;
    box-shadow: 0px 1px 4px rgba(0, 0, 58, 0.08), 0px 1px 1px -1px rgba(0, 0, 58, 0.08), inset 0px -1px 0px rgba(0, 0, 0, 0.08);
    border-radius: 8px;
    padding-left:12px;
    padding-right:12px;
    padding-top: 8px;
    padding-bottom: 8px;
    color:#00003A;
    transition: box-shadow 0.1s ease-in-out;
    &.has-icon {
      padding-left:40px;
    }

    &:hover {
      border: 1px solid $color-neutral-grey-30;
    }
    &:focus {
      outline: none;
      border: 2px solid $color-brand;
      box-shadow: 0px 0px 0px 2px rgba(50, 25, 178, 0.24);
      caret-color: $color-brand;
      margin-left:-1px;
    }
    &::placeholder { 
      font-weight: 400;
      font-size: 14px; 
      color: $color-neutral-grey-60;
    }

    &.has-error {
      border: 1px solid $color-orange;
      &:focus {
        outline: none;
        box-shadow: inset 0px -2px 0px $color-orange ;
      }
    }

    &.has-error-fixed {
      border: 1px solid $color-green;
      &:focus {
        outline: none;
        box-shadow: inset 0px -2px 0px $color-green ;
      }
    }
    &.disabled {
      // background: $color-neutral-grey-4;
      background: none;
      color: $color-neutral-grey-60;
      border:none;
      pointer-events: none;
      box-shadow: none;
      &.disabled-visible {
        background: $color-neutral-grey-4;
        border: 1px solid $color-neutral-grey-8;
      }
    }
  }
}

</style>
<style lang="scss">
@import '@/css/variables';
.custom-number-input-buttons {
  button {
    &:disabled {
      .icon {
        background-color: $color-neutral-grey-30 !important;
      }
    }
  }
}
</style>